import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { ReviewServicePorts } from "../../model";

import { URLS } from "./consts";
import {
  Approve,
  Create,
  Delete,
  Get,
  GetByOrder,
  GetForAdmin,
  GetPaginated,
  GetPaginatedForAdmin,
  Update,
} from "./ios";
import { mapDtoToEntity, mapDtoToEntity2 } from "./mappers";

import { Request, Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends ReviewServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create({
    text,
    customer: { email, phone, ...customer },
    ...params
  }: ReviewServicePorts.ProviderIos.Create.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    ReviewServicePorts.ProviderIos.Common.OutSchema
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.create, {
            reviewContent: text,
            contactEmail: email,
            contactPhone: phone,
            ...customer,
            ...params,
          }),
        Create.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400 && body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override update({
    text,
    ...params
  }: ReviewServicePorts.ProviderIos.Update.In): taskEither.TaskEither<
    | UnexpectedError
    | AuthenticationError
    | NotFoundError
    | ReviewServicePorts.ProviderIos.Update.Error,
    ReviewServicePorts.ProviderIos.Common.OutSchema
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.put(URLS.update, {
            reviewContent: text,
            ...params,
          }),
        Update.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400 && body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND")) {
          if (body.errorDetails.debugMessage?.includes("Замовлення"))
            return taskEither.left(
              new ReviewServicePorts.ProviderIos.Update.Error(
                ReviewServicePorts.ProviderIos.Update.ErrorReason.ORDER_NOT_FOUND,
              ),
            );

          return taskEither.left(new NotFoundError());
        }
        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: ReviewServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override approve(
    params: ReviewServicePorts.ProviderIos.Approve.In,
  ): taskEither.TaskEither<UnexpectedError | AuthenticationError | NotFoundError, void> {
    return function_.pipe(
      Response.process(() => this.axios.put(URLS.approve(params)), Approve.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override get({
    id,
  }: ReviewServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    ReviewServicePorts.ProviderIos.Common.OutSchema
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.get(id)), Get.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getForAdmin({
    id,
  }: ReviewServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    ReviewServicePorts.ProviderIos.Common.OutSchema2
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getForAdmin(id)), GetForAdmin.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity2(response.body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getPaginated(
    params: ReviewServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<UnexpectedError, ReviewServicePorts.ProviderIos.GetPaginated.Out> {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap(({ body }) => {
        const {
          data: { items, totalCount },
        } = body;
        const {
          success,
          data: pageMeta,
          error,
        } = Pagination.Meta.zSchema.safeParse({
          options: params.pagination,
          totalItems: totalCount,
        });

        if (!success) return taskEither.left(new UnexpectedError(error));

        return taskEither.right({
          items: items.map(mapDtoToEntity),
          meta: pageMeta,
        } satisfies ReviewServicePorts.ProviderIos.GetPaginated.Out);
      }),
    );
  }

  override getPaginatedForAdmin(
    params: ReviewServicePorts.ProviderIos.GetPaginatedForAdmin.In,
  ): taskEither.TaskEither<
    UnexpectedError,
    ReviewServicePorts.ProviderIos.GetPaginatedForAdmin.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginatedForAdmin,
            Request.ListParams.mapListParams(params, GetPaginatedForAdmin.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginatedForAdmin.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) {
          const {
            data: { items, totalCount },
          } = response.body;
          const {
            success,
            data: pageMeta,
            error,
          } = Pagination.Meta.zSchema.safeParse({
            options: params.pagination,
            totalItems: totalCount,
          });

          if (!success) return taskEither.left(new UnexpectedError(error));

          return taskEither.right({
            items: items.map(mapDtoToEntity2),
            meta: pageMeta,
          } satisfies ReviewServicePorts.ProviderIos.GetPaginatedForAdmin.Out);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getByOrder({
    orderNumber,
  }: ReviewServicePorts.ProviderIos.GetByOrder.In): taskEither.TaskEither<
    UnexpectedError | NotFoundError,
    ReviewServicePorts.ProviderIos.GetByOrder.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getByOrder(orderNumber)), GetByOrder.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(body.data.map(mapDtoToEntity));

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}
export { ApiProvider };
